// Static brand content + shared constants used across pages.

export const BRAND = {
  name: "Beyond Potentials",
  tagline: "By bankers, for bankers",
  positioning:
    "The only banker-founded talent partner for Monaco & Geneva private banking.",
  phoneMonaco: "+377 97 00 00 00",
  phoneGeneva: "+41 22 000 00 00",
  email: "contact@beyondpotentials.com",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  {
    label: "Services",
    to: "/services",
    children: [
      { label: "Retained Executive Search", to: "/services/recruitment" },
      { label: "Talent Development", to: "/services/talent-development" },
      { label: "Talent Strategy Consulting", to: "/services/consulting" },
      { label: "The Beyond Partnership", to: "/services/beyond-partnership" },
    ],
  },
  {
    label: "Markets",
    to: "/markets",
    children: [
      { label: "Monaco", to: "/markets/monaco" },
      { label: "Geneva", to: "/markets/geneva" },
      { label: "Paris", to: "/markets/paris" },
    ],
  },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Insights", to: "/insights" },
  { label: "Tools", to: "/tools" },
];

export const IMAGES = {
  heroTeam: "/brand/team-office.jpg",
  heroCity: "https://images.unsplash.com/photo-1534447677768-be436bb09401?auto=format&fit=crop&w=2400&q=80",
  monaco: "https://images.unsplash.com/photo-1592861956120-e524fc739696?auto=format&fit=crop&w=2000&q=80",
  monacoYacht: "https://images.unsplash.com/photo-1602002418082-a4443e081dd1?auto=format&fit=crop&w=2000&q=80",
  geneva: "https://images.unsplash.com/photo-1605640840605-14ac1855827b?auto=format&fit=crop&w=2000&q=80",
  genevaAlt: "https://images.unsplash.com/photo-1558271736-cd043ef2e855?auto=format&fit=crop&w=2000&q=80",
  paris: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=2000&q=80",
  architecture: "https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=2000&q=80",
  glass: "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?auto=format&fit=crop&w=2000&q=80",
  marble: "https://images.unsplash.com/photo-1577495508048-b635879837f1?auto=format&fit=crop&w=2000&q=80",
  boardroom: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=2000&q=80",
  meeting: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=2000&q=80",
  office: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80",
  founder: "/founder.webp",
  founderAlt: "/brand/img1.jpg",
  mediaBFM: "/brand/img2.jpg",
  mediaCFA: "/brand/img4.jpg",
};

export const SERVICES = [
  {
    slug: "recruitment",
    name: "Retained Executive Search",
    short: "Recruitment",
    tagline: "Hire bankers who arrive revenue-ready.",
    icon: "Target",
    summary:
      "Discreet, single-mandate retained search for senior relationship managers, team heads and investment leaders — assessed by people who have done the job.",
    points: [
      "Single-mandate focus — your search is never one of forty open roles",
      "Genuine passive-candidate access through banking relationships",
      "Realistic portable-book assessment, not optimistic CVs",
      "47-touchpoint process from briefing to 12-month follow-up",
    ],
  },
  {
    slug: "talent-development",
    name: "Talent Development",
    short: "Development",
    tagline: "Keep the talent you fought to hire.",
    icon: "TrendingUp",
    summary:
      "Leadership development, coaching and team workshops designed for front-office banking realities — led by former practitioners.",
    points: [
      "Leadership development for team heads and rising RMs",
      "1:1 executive coaching with banking context",
      "Team dynamics and culture workshops",
      "Retention diagnostics that find the real cause",
    ],
  },
  {
    slug: "consulting",
    name: "Talent Strategy Consulting",
    short: "Consulting",
    tagline: "Decisions informed by real market intelligence.",
    icon: "LineChart",
    summary:
      "Compensation benchmarking, retention strategy and succession planning built on proprietary Monaco-Geneva data.",
    points: [
      "Monaco-Geneva compensation benchmarking",
      "Retention and counter-offer strategy",
      "Succession and workforce planning",
      "Org design for new market entry",
    ],
  },
  {
    slug: "beyond-partnership",
    name: "The Beyond Partnership",
    short: "Beyond Partnership",
    tagline: "One partner, the full talent lifecycle.",
    icon: "Infinity",
    summary:
      "An integrated annual retainer combining search, development and consulting — a single relationship across the entire employee lifecycle.",
    points: [
      "Priority access to retained search capacity",
      "Ongoing leadership development programme",
      "Quarterly market intelligence briefings",
      "A single, accountable talent partner",
    ],
    premium: true,
  },
];

export const LIFECYCLE = [
  {
    step: "01",
    title: "Strategic Hire",
    desc: "We identify and place the passive, pre-qualified talent that builds your AuM — discreetly and fast.",
    deliverables: ["Retained search", "Portable-book assessment", "Cultural-fit evaluation"],
  },
  {
    step: "02",
    title: "Leadership Development",
    desc: "We develop the people you hire into the leaders who retain teams and grow books over time.",
    deliverables: ["Executive coaching", "Leadership programmes", "Team workshops"],
  },
  {
    step: "03",
    title: "Retention Strategy",
    desc: "We advise on compensation, succession and culture so your best people stay — and keep performing.",
    deliverables: ["Compensation benchmarking", "Succession planning", "Retention diagnostics"],
  },
];

export const VALUES = [
  {
    title: "Insider, not outsider",
    desc: "Every consultant has genuine front-office banking experience. We have sat on your side of the table.",
    icon: "ShieldCheck",
  },
  {
    title: "Discretion by default",
    desc: "Single-mandate, confidential search that protects your clients, your team and your reputation.",
    icon: "Lock",
  },
  {
    title: "Data-informed judgement",
    desc: "Proprietary Monaco-Geneva intelligence underpins every recommendation we make.",
    icon: "LineChart",
  },
  {
    title: "The full lifecycle",
    desc: "We do not place and disappear. We stay — because your people's potential goes far beyond their first day.",
    icon: "Infinity",
  },
];

export const OFFICES = [
  {
    city: "Monaco",
    lines: ["Beyond Potentials", "Carré d'Or", "98000 Monaco"],
    phone: "+377 97 00 00 00",
  },
  {
    city: "Geneva",
    lines: ["Beyond Potentials", "Rue du Rhône", "1204 Geneva, Switzerland"],
    phone: "+41 22 000 00 00",
  },
  {
    city: "Paris",
    lines: ["Beyond Potentials", "8e arrondissement", "75008 Paris, France"],
    phone: "+33 1 00 00 00 00",
  },
];

export const FOUNDER = {
  name: "Celine Corletti",
  role: "Founder & Managing Partner",
  credentials: ["ESCP Europe", "15+ years in banking & search", "Ex-Structured Products Sales"],
  bio: [
    "Celine Corletti spent over a decade inside Tier-1 private banking — in structured products sales, working alongside the relationship managers and team heads she now places — before founding Beyond Potentials.",
    "That practitioner background is the firm's defining advantage. Fewer than 5% of competing search firms in Geneva can claim genuine front-office banking experience, and none in Monaco combine it with dual-market coverage.",
    "She founded Beyond Potentials on a simple conviction: that the people who identify banking talent should have built banking careers themselves.",
  ],
};

export const CAREERS_FAQ = [
  {
    q: "Is my application confidential?",
    a: "Absolutely. We never share your details with any institution without your explicit consent on a role-by-role basis. Discretion is the foundation of our candidate relationships.",
  },
  {
    q: "I am not actively looking. Should I still talk to you?",
    a: "Yes. The majority of our placements are passive candidates who were not browsing job boards. A confidential conversation now means we understand your situation when the right opportunity appears.",
  },
  {
    q: "What roles do you typically place?",
    a: "Senior relationship managers, team and market heads, investment advisors, and senior control-function leaders across Monaco, Geneva and Paris.",
  },
  {
    q: "How do you assess portability?",
    a: "Honestly. We model realistic transfer rates with every candidate, because an over-promised book damages everyone — you included.",
  },
];
